import { Grid, Skeleton } from '@mui/material';
import { Field, Formik } from 'formik';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useMutation, useQueryClient } from 'react-query';
import {
	CerebroDrawer,
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroFileDragAndDrop,
	CerebroForm,
	CerebroHeading,
	CerebroLabel,
	CerebroLink,
	CerebroPrimaryButton,
	CerebroText,
} from '../../shared/components';
import { useApi } from '../../shared/context';
import { QueryKeys, useActiveDataRoomUsersQuery } from '../../shared/hooks';
import { DiligenceRequestType, DiligenceRequestTypeMap, StagedDiligenceRequestUpload } from '../../shared/models';
import { FormikUtils, HtmlUtils } from '../../shared/utils';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { getApiResponseData } from '../../shared/utils/axios';

interface UploadDiligenceRequestFormProps {
	dataRoomGuid: string | undefined;
	open: boolean;
	close: () => void;
}

export const UploadDiligenceRequestForm: React.FC<UploadDiligenceRequestFormProps> = ({
	dataRoomGuid,
	open,
	close,
}) => {
	const api = useApi();
	const client = useQueryClient();
	const snackbar = useSnackbar();
	const ref = React.useRef<HTMLDivElement>(null);
	const lenderQuery = useActiveDataRoomUsersQuery(dataRoomGuid);
	const templateUrl = `${process.env.PUBLIC_URL}/content/DataRoomDiligenceTemplate.xlsx`;

	const uploadMutation = useMutation(
		async ({ values }: UploadDiligenceRequestFormMutationVariables) => {
			let filesUpdate: StagedDiligenceRequestUpload | null = null;

			const uploads = values.uploads;

			if (uploads && uploads.length > 0) {
				let formData = new FormData();
				formData.append('dataRoomUserGuid', values.dataRoomUserGuid);
				formData.append('diligenceType', values.diligenceType);

				uploads.forEach(upload => {
					formData.append('uploads', upload, upload.name);
				});

				const { data, error } = await getApiResponseData<StagedDiligenceRequestUpload>(
					api.post(CerebroApiEndpoints.DataRoomDiligenceRequests.Uploads(dataRoomGuid), formData, {
						headers: { 'Content-Type': 'multipart/form-data' },
					})
				);

				if (error) {
					throw new Error(error.response?.data);
				}

				return data;
			}

			return filesUpdate;
		},
		{
			onSuccess: () => {
				client.invalidateQueries(QueryKeys.DataRoomDiligenceRequests.Uploads(dataRoomGuid));
				snackbar.enqueueSnackbar('Diligence requests have been uploaded.', { variant: 'success' });
				close();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer open={open} onClose={close}>
			<CerebroHeading size="h1" component="h1">
				Upload Diligence Requests
			</CerebroHeading>

			<Formik
				initialValues={{
					dataRoomUserGuid: '',
					diligenceType: 'DocumentRequest' as DiligenceRequestType,
					uploads: [] as File[],
				}}
				onSubmit={FormikUtils.MutateOnSubmit(uploadMutation.mutate)}
			>
				{({ isSubmitting, errors, touched, handleSubmit }) => {
					return (
						<CerebroForm
							flatIndicator
							encType="multipart/form-data"
							onSubmit={handleSubmit}
							sx={{ marginTop: 3, paddingRight: 3 }}
							scrollbarOffset={HtmlUtils.CalculateScrollbarOffset(ref.current)}
						>
							<Grid
								container
								spacing={3}
								sx={{ marginTop: 3 }}
								justifyContent={{ xs: 'center', md: 'flex-start' }}
							>
								<Grid item xs={11}>
									<CerebroLabel required id="dataRoomUserGuid">
										Lender
									</CerebroLabel>
									{lenderQuery.isLoading && <Skeleton height={60} />}
									{lenderQuery.isSuccess && (
										<Field
											id="dataRoomUserGuid"
											as={CerebroDropdown}
											name="dataRoomUserGuid"
											disabled={isSubmitting}
											error={FormikUtils.IsInvalid('dataRoomUserGuid', errors, touched)}
										>
											{lenderQuery.data.map(lender => (
												<CerebroDropdownMenuItem key={lender.guid} value={lender.guid}>
													{lender.fullName} ({lender.bankName})
												</CerebroDropdownMenuItem>
											))}
										</Field>
									)}
									{FormikUtils.IsInvalid('dataRoomUserGuid', errors, touched) && (
										<CerebroText variant="error">{errors.dataRoomUserGuid}</CerebroText>
									)}
								</Grid>

								<Grid item xs={11}>
									<CerebroLabel required id="diligenceType">
										Type
									</CerebroLabel>
									<Field
										id="diligenceType"
										as={CerebroDropdown}
										name="diligenceType"
										disabled={isSubmitting}
									>
										{Object.entries(DiligenceRequestTypeMap).map(([value, description]) => (
											<CerebroDropdownMenuItem key={value} value={value}>
												{description}
											</CerebroDropdownMenuItem>
										))}
									</Field>
								</Grid>

								<Grid item xs={11}>
									<CerebroLabel required id="uploads">
										Select File to Upload
									</CerebroLabel>
									<Field
										as={CerebroFileDragAndDrop}
										name="uploads"
										onlyPdf={false}
										multiple={true}
										sx={{ marginY: 3, width: '100%' }}
									/>
								</Grid>

								<Grid item xs={11}>
									<CerebroText variant="hint">
										Upload must use the template excel file, download below if needed.
									</CerebroText>
									<CerebroLink href={templateUrl} target="_blank" sx={{ cursor: 'pointer' }}>
										Download Template File
									</CerebroLink>
								</Grid>

								<Grid item xs={8}>
									<CerebroPrimaryButton loading={isSubmitting} type="submit">
										Upload File &amp; Proceed
									</CerebroPrimaryButton>
								</Grid>
							</Grid>
						</CerebroForm>
					);
				}}
			</Formik>
		</CerebroDrawer>
	);
};

type UploadDiligenceRequestFormMutationVariables =
	FormikUtils.MutateOnSubmitVariables<UploadDiligenceRequestFormValues>;
interface UploadDiligenceRequestFormValues {
	dataRoomUserGuid: string;
	diligenceType: DiligenceRequestType;
	uploads: File[];
}
